import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DynamicObjectInterface } from '../../../../sdk/type';
import { useAPICall } from '../../../../hooks';
import {
  ButtonPropsTypes,
  ButtonSize,
  ButtonStyles,
  Card,
  DetailCardProgress,
  HeaderStyle,
  Tag,
  TagStyle,
  Theme,
  IconStyle,
  ButtonIconPosition,
  useBreakpoint,
} from '../../../../ui/components';
import ShareDrawer from '../../../../ui/components/DrawerAlt/ShareDrawer';
import ParticipantService from '../../../../sdk/services/participantService';
import TeamService from '../../../../sdk/services/teamService';
import { UserData } from '../../dashboardConfig';
import { formatDateDMYYYY, LoggerService, PAGES, trackPage } from '../../../../ui/utils';
import Error from '../../../../assets/image/icons/ico_error_orange.svg';
import { MenuDataType } from '../../../home/Home';
import AttachmentService from '../../../../sdk/services/attachmentService';
import { HeaderFlex } from '../../../../ui/components/Form/Card/types/enums';
import AuditLogService from '../../../../sdk/services/auditLogService';
import AuditHistoryAccordion, { AuditLogEntry } from '../../../admin/audit-log/auditLogAccordion';
import styles from './index.module.scss';
interface TeamDetailsPropsType {
  index: number;
  decodedData: DynamicObjectInterface;
  dataCampaign: MenuDataType;
  role: 'LEADER' | 'COACH' | 'PARTICIPANT';
  myTeamId?: string;
}

export const TeamDetails = (props: TeamDetailsPropsType) => {
  const { dataCampaign, index, role, decodedData, myTeamId } = props;
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const apiCall = useAPICall();
  //const { adminToast } = useAdminToast();
  const viewPortType = useBreakpoint();
  const isDesktopView = ['xs', 'sm', 'md'].indexOf(viewPortType) === -1;
  const [data, setData] = useState<UserData | null>(null);
  const [emailHistory, setEmailHistory] = useState<AuditLogEntry[]>([]);
  const [isCoach, setIsCoach] = useState(false);
  const [profileImageUrls, setProfileImageUrls] = useState<Record<string, string | undefined>>({});

  const paths = location.pathname.split('/');
  const teamsIdx = paths.indexOf('teams');
  const [showShareDrawer, setShowShareDrawer] = useState(false);
  const [joinTeamLink, setJoinTeamLink] = useState<string>('');
  const [campaignRecId, setCampaignRecId] = useState(null);
  //  const [isParticipant, setIsParticipant] = useState(false);
  // const [isLeader, setIsLeader] = useState(false);
  // const [showCoachList, setShowCoachList] = useState(false);
  // const [filteredCoachList, setFilteredCoachList] = useState(masterList?.coachList);
  // const [masterList, setMasterList] = useState<{
  //   coachList: DynamicObjectInterface;
  // }>();
  //const [formData, setFormData] = useState<UserData | null>(JSON.parse(JSON.stringify(data)) as UserData);

  let campaignId = '';
  let teamId = '';

  if (teamsIdx !== -1 && teamsIdx + 1 < paths.length) {
    campaignId = decodeURIComponent(paths[teamsIdx - 1]);
    teamId = decodeURIComponent(paths[teamsIdx + 1]);
  }

  if (myTeamId) {
    const myTeamsIdx = paths.indexOf('myTeam');
    campaignId = decodeURIComponent(paths[myTeamsIdx - 1]);
    teamId = myTeamId;
  }

  const fetchData = async () => {
    let teamData = await apiCall(ParticipantService.getTeamParticipants, {
      campaignId: campaignId,
      teamId: teamId,
    });

    let participantData: DynamicObjectInterface = [];

    if (teamData?.data?.data) {
      participantData = await Promise.all(
        teamData?.data?.data?.map(async (teamItem: DynamicObjectInterface) => {
          let participant = await apiCall(
            ParticipantService.getParticipantDetail,
            { campaignId: campaignId, teamId: teamId },
            teamItem.userId.toString(),
          );
          return participant?.data?.data;
        }),
      );
    }

    let campaignData = await apiCall(TeamService.getTeamDetails, `${teamId}`);
    setCampaignRecId(campaignData?.data?.data.campaignRecId);
    setData((i: DynamicObjectInterface) => {
      i = i || {};

      i.isCoachEnrollment = dataCampaign?.enrollment.some(
        (i: DynamicObjectInterface) => i.enrollmentType === 'COACH' || i.enrollmentType === 'LEADER',
      );
      i.teamStartDate =
        teamData?.data?.data?.[index]?.teamDetail?.teamStartDate ?? campaignData?.data?.data?.teamStartDate;
      i.teamEndDate = teamData?.data?.data?.[index]?.teamDetail?.teamEndDate ?? campaignData?.data?.data?.teamEndDate;
      i.participantDetails = teamData?.data?.data;
      if (role === 'COACH') {
        participantData?.forEach((participant: DynamicObjectInterface, index: number) => {
          if (participant) {
            let participantToUpdate = i.participantDetails[index];
            let productPoints = (participant.point?.purchase ?? 0) + (participant.point?.purchaseAdj ?? 0);
            let resultPoints = (participant.point?.winning ?? 0) + (participant.point?.winningAdj ?? 0);
            let subscribePoints = (participant.point?.subscribe ?? 0) + (participant.point?.subscribeAdj ?? 0);
            let totalPoints = productPoints + resultPoints + subscribePoints;

            participantToUpdate.points = {
              product: productPoints,
              result: resultPoints,
              subscribe: subscribePoints,
              totalPoints: totalPoints,
            };
          }
        });
      }
      i.teamLeader = teamData?.data?.data?.[index]?.teamDetail?.teamLeader;
      i.teamSize = campaignData?.data?.data?.teamSize;
      i.teamName = teamData?.data?.data?.[index]?.teamDetail.teamName ?? campaignData?.data?.data?.teamName;
      i.teamCoachId = teamData?.data?.data?.[index]?.teamDetail?.coachId ?? campaignData?.data?.data?.coachId;
      i.teamCoachName = teamData?.data?.data?.[index]?.teamDetail?.coachName ?? campaignData?.data?.data?.coachName;
      i.teamDescription = teamData?.data?.data?.[0]?.teamDetail?.teamDescription;
      // i.teamLeaderLoginId = teamData?.data?.data[0]?.teamDetail?.teamLeaderLoginId;
      i.teamLeaderLoginId = participantData?.[0]?.team?.teamLeaderUserId;
      i.teamStatus = campaignData?.data?.data?.status;

      return { ...i };
    });

    const emailHistory = await apiCall(AuditLogService.fetchEmailHistoryLog, {
      campaignId,
      teamId,
    });
    setEmailHistory(emailHistory?.data?.data);
  };

  useEffect(() => {
    // setIsParticipant(false);
    setIsCoach(false);
    // setIsLeader(false);
    dataCampaign?.enrollment.forEach(enrollment => {
      switch (enrollment?.enrollmentType) {
        //   case 'PARTICIPANT':
        //     setIsParticipant(true);
        //     break;
        case 'COACH':
          setIsCoach(true);
          break;
        //   case 'LEADER':
        //     setIsLeader(true);
        //     break;
      }
      // if (enrollment?.enrollmentType === 'COACH') {
      //   setIsCoach(true);
      // }
    });
    fetchData();

    const timeout = setTimeout(() => {
      trackPage(PAGES.teamDetails);
    }, 1000);
    return () => {
      clearTimeout(timeout);
    };
  }, [location]);

  // const goBack = () => {
  //   let idx = location.pathname.indexOf('/teams');
  //   if (idx !== -1) {
  //     let url = location.pathname.substring(0, idx + '/teams'.length);
  //     navigate(url);
  //   }
  // };

  const getButtonConfig = useMemo(() => {
    return {
      addParticipant: [
        {
          title: t('dashboard.my-team.button.addParticipant'),
          buttonStyle: ButtonStyles.SOLID,
          theme: Theme.GREEN,
          size: isDesktopView ? ButtonSize.SMALL : ButtonSize.EXTRA_SMALL,
          onClick: () => {
            navigate(`/campaigns/teams/add-participant?=${teamId}`);
          },
        },
        {
          title: t('dashboard.my-team.button.share'),
          buttonStyle: ButtonStyles.OUTLINE,
          icon: { iconName: 'share-alt', iconStyle: IconStyle.SOLID },
          iconPosition: ButtonIconPosition.LEFT,
          theme: Theme.GREEN,
          size: isDesktopView ? ButtonSize.SMALL : ButtonSize.EXTRA_SMALL,
          onClick: () => {
            generateJoinTeamLink();
          },
        },
      ],
    } as { [index: string]: ButtonPropsTypes[] };
  }, [campaignRecId]);

  const fetchProfileImage = async (userId: number) => {
    try {
      const response = await AttachmentService.downloadProfileImage(userId ?? '');
      if (response.data && response.data.data && response.data.data.image) {
        setProfileImageUrls(prevState => ({
          ...prevState,
          [userId]: response.data.data.image,
        }));
      }
    } catch (error) {
      LoggerService.log('Error downloading image', error);
    }
  };

  useEffect(() => {
    data?.participantDetails?.forEach(participant => {
      fetchProfileImage(participant.userId);
    });
  }, [data?.participantDetails]);

  const generateJoinTeamLink = () => {
    if (teamId && campaignRecId) {
      const params = `${campaignRecId},${teamId},0`;
      const encodedParam = btoa(params);
      setJoinTeamLink(`${window.location.protocol}//${window.location.host}?source=email&t=${encodedParam}`);
      setShowShareDrawer(true);
    }
  };

  const sharePrompt = (
    <>
      {showShareDrawer && (
        <ShareDrawer
          open={!!joinTeamLink}
          onClose={() => setShowShareDrawer(false)}
          title={t('dashboard.my-team.button.shareVia')}
          programMessage={t('dashboard.my-team.label.invitedToJoinCampaign')
            .replace('{campaignName}', dataCampaign?.campaignName)
            .replace('{join-team-link}', joinTeamLink)}
          programUrl={joinTeamLink}
          channels={process.env.REACT_APP_COUNTRY === 'PH' ? ['FaWhatsapp', 'FaViber','FaLink'] : ['FaWhatsapp', 'FaLink']}
          programTitle=""
        />
      )}
    </>
  );

  return (
    <div className={styles['team-detail-container']}>
      <Card
        title={t('dashboard.my-team.label.teamDetails')}
        headerStyle={isDesktopView ? HeaderStyle.HEADER_5 : HeaderStyle.HEADER_6}
        headerFlex={isDesktopView ? HeaderFlex.START : undefined}
        button={
          data &&
          data.participantDetails &&
          data.participantDetails.filter(participant => participant.status === 'ACTIVE').length <
            Number(data?.teamSize) &&
          isCoach
            ? getButtonConfig.addParticipant
            : undefined
        }
      >
        {data &&
          data.participantDetails &&
          data.participantDetails.filter(participant => participant.status === 'ACTIVE').length <
            Number(data?.teamSize) && (
            <div className={styles['team-notification']}>
              <img
                src={Error}
                width={'32px'}
                height={'32px'}
                className={styles['team-notification-icon']}
                alt={'warning'}
              />
              <div>{t('dashboard.my-team.label.incompleteTeamNotification')}</div>
            </div>
          )}
        <div className={styles['team-details-container']}>
          <div className={styles['team-details-title-container']}>
            <div className={styles['team-details-coach']}>
              <Tag text={`${dataCampaign?.campaignName}`} theme={Theme.GRAY} style={TagStyle.SOLID} />
            </div>
          </div>
          <div className={styles['']}>
            <div className={styles['team-details-coach']}>
              {t('dashboard.my-team.label.teamName')}
              <div className={styles['team-details-date-range']}> {data?.teamName}</div>
            </div>
            <div className={styles['team-details-coach']}>
              {t('dashboard.my-team.label.teamDate')}
              <div className={styles['team-details-date-range']}>{`${
                data?.teamStartDate
                  ? formatDateDMYYYY(new Date(data?.teamStartDate))
                  : t('dashboard.my-team.label.dateNotAvailable')
              } - ${data?.teamEndDate ? formatDateDMYYYY(new Date(data?.teamEndDate)) : t('dashboard.my-team.label.dateNotAvailable')}`}</div>
            </div>
          </div>
          <div>
            {data &&
              data.participantDetails &&
              data.participantDetails.length > 0 &&
              data?.participantDetails
                //?.filter(participant => participant.status === 'ACTIVE') // Filter out inactive participants
                .map((participant, index) => (
                  <div key={index} className={styles['team-details-progress-card']}>
                    <DetailCardProgress
                      coachEnrollmentDate={data?.coachEnrollmentDate}
                      isCoachEnrollment={data?.isCoachEnrollment}
                      points={data?.points}
                      profileImageUrl={profileImageUrls[participant.userId]}
                      {...participant}
                      handleRedirect={() =>
                        decodedData.userId === participant.userId
                          ? navigate(`/campaigns/${campaignId}`)
                          : myTeamId
                            ? navigate(`user?id=${participant.userId}&tId=${myTeamId}`)
                            : navigate(`user?id=${participant.userId}`)
                      }
                    />
                  </div>
                ))}
          </div>
        </div>
        {sharePrompt}
      </Card>

      {isCoach && (
        <Card>
          <AuditHistoryAccordion
            auditHistoryLog={emailHistory}
            displayAuditEmpty={emailHistory?.length <= 0}
            source={'dashboard'}
          />
        </Card>
      )}
    </div>
  );
};
